import React, {
    useState, useEffect, useMemo, useCallback,
} from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragDropContext } from 'react-beautiful-dnd';
import {
    Button, ButtonGroup, ButtonToolbar, Col, Row, ToggleButton, ToggleButtonGroup,
} from 'react-bootstrap';
import ReactTable from 'react-table';
import { toast } from 'react-toastify';

import PocketForm from './PocketForm';
import { DragTrComponent, DropTbodyComponent } from '../../blocks/table_block';
import Fetcher from '../../common/communicate';
import dataUrlToFile from '../../tools/dataUrlToFile';
import uploadImage from '../../tools/uploadImage';

const LANGS = ['ru', 'en'];

export default function Pocket({
    itemsLimit, endpoint, withSingleActive,
    filterFields, formFields, renderColumns,
    renderRightColumn, isFlex, noDrag,
}) {
    const [rows, setRows] = useState([]);
    const [rowsFiltered, setRowsFiltered] = useState([]);
    const [currentItem, setItem] = useState({});
    const [currentLang, setLang] = useState('ru');
    const [currentFilters, setFilters] = useState({});
    const [invalidFields, setInvalidFields] = useState([]);
    const [lastEditTime, setLastEditTime] = useState(null);
    const [isTableLoading, setTableLoading] = useState(false);

    useEffect(() => {
        Fetcher(endpoint, 'GET', handleData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint]);

    useEffect(() => {
        filterItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, currentFilters]);

    const itemsCount = useMemo(() => rows.length, [rows.length]);

    const isMultilang = useMemo(() => formFields.some((field) => field.isMultilang), [formFields]);

    const trProps = useCallback((state, rowInfo) => ({ rowInfo }), []);

    function handleData(data) {
        if (!data || (!data.data && !data.results)) { // TODO: оставить на беке только data
            return;
        }

        let rows = data.data || data.results?.items || data.results || [];

        if (isFlex) { // TODO: это временный костыль, чтобы удалить записи без id. Через пару недель можно выпилить
            rows = rows.filter((row) => row.id);
        }

        setRows(rows);
        setTableLoading(false);
        setLastEditTime(data.results?.last_edited_at || data.last_edited_at);
    }

    async function storeData(newRows) {
        Fetcher(endpoint, 'PATCH', handleData, {
            data: newRows.slice(0, itemsLimit),
            last_edited_at: lastEditTime,
        });
    }

    function saveItem() {
        const validation = validate();

        if (!validation.isValid) {
            let message = '';

            Object.entries(validation.fields).map(([lang, langData]) => {
                if (lang === 'multi' && langData?.length) {
                    message += `Не заполнены общие поля: ${langData.join(', ')}. `;
                } else if (langData?.length) {
                    message += `Для языка ${lang} не заполнены поля: ${langData.join(', ')}. `;
                }
            });

            setInvalidFields(validation.fields);

            toast.error(message);

            return;
        }

        addItemToRows(currentItem);
        setInvalidFields({});
    }

    function editItem(e) {
        const { id } = e.currentTarget.dataset;
        const clonedRows = JSON.parse(JSON.stringify(rows));

        const entityObj = clonedRows.find((item) => +item.id === +id);

        if (!entityObj) {
            return;
        }

        setItem(entityObj);
    }

    function deleteItem(event) {
        const { title, id } = event.currentTarget.dataset;

        if (window.confirm(`Внимание! Вы действительно хотите удалить ${title || ''}?`)) {
            removeItemFromRows(id);
        }
    }

    function addItemToRows(newRow) {
        setTableLoading(true);
        let clonedRows = JSON.parse(JSON.stringify(rows));

        if (newRow.id) {
            const editedItemIndex = clonedRows.findIndex((item) => item.id === newRow.id);

            clonedRows[editedItemIndex] = newRow;
        } else {
            if (isFlex) {
                newRow.id = Date.now();
            }

            clonedRows.push(newRow);

            clonedRows = sortRows(clonedRows, clonedRows.length - 1, 0);
        }

        resetItem();
        setRows(clonedRows);
        storeData(clonedRows, newRow, newRow.id ? 'PUT' : 'POST', rows);
    }

    function removeItemFromRows(id) {
        setTableLoading(true);
        let clonedRows = JSON.parse(JSON.stringify(rows));

        const deletedItemIndex = clonedRows.findIndex((item) => +item.id === +id);

        clonedRows = sortRows(clonedRows, deletedItemIndex, clonedRows.length - 1);

        clonedRows = clonedRows.slice(0, -1);

        setRows(clonedRows);
        storeData(clonedRows);
    }

    function removeCheckedItemsFromRows() {
        const filteredRows = rows.filter(({ pos_checked }) => !pos_checked);

        setRows(filteredRows);
        storeData(filteredRows);
    }

    function resetItem() {
        setItem({});
    }

    function filterItems() {
        const filter = Object.entries(currentFilters);

        const keys = {};
        // добавления валидаторов для параметров разного типа данных
        filter.forEach(([key, value]) => {
            if (value instanceof Array) {
                keys[key] = {
                    rowValidator: (row) => !value?.length || value.some((rowVal) => row[key].some((filterVal) => rowVal.id === filterVal.id)),
                };
            } else if (typeof value === 'string' || typeof value === 'number') {
                keys[key] = {
                    rowValidator: (row) => !value || new Date(row[key]).toDateString() === new Date(value).toDateString(),
                };
            } else {
                keys[key] = { rowValidator: () => true };
            }
        });
        // функция, по которой происходит фильтрация строк в таблице
        const rowValidators = (row) => Object.keys(keys).every((key) => keys[key].rowValidator(row));
        // фильтрация массива строк
        const rowsFiltered = rows.filter((row) => rowValidators(row));

        setRowsFiltered(rowsFiltered);
    }

    function handleChange(e) {
        const { value, checked } = e.currentTarget;
        const isCheckbox = e.currentTarget.getAttribute && e.currentTarget.getAttribute('type') === 'checkbox';

        const newValue = isCheckbox ? checked : value;

        setItem({
            ...currentItem,
            [e.currentTarget.id]: newValue,
        });
    }

    function handleLangDataChange(e) {
        const newItem = JSON.parse(JSON.stringify(currentItem));

        newItem.content = {
            ...(newItem.content || []),
            [currentLang]: {
                ...((newItem.content && newItem.content[currentLang]) || []),
                [e.currentTarget.id]: e.currentTarget.value,
            },
        };

        setItem(newItem);
    }

    function handleDateChange(dateKey, date) {
        setItem({
            ...currentItem,
            [dateKey]: date,
        });
    }

    async function handleImageChange({ id, value, isMultilang }) {
        const file = await dataUrlToFile(value, `pocket-${Date.now()}`);
        const uploadedImage = await uploadImage(file);

        const newItem = JSON.parse(JSON.stringify(currentItem));

        if (isMultilang) {
            newItem.content = {
                ...(newItem.content || []),
                [currentLang]: {
                    ...((newItem.content && newItem.content[currentLang]) || []),
                    [id]: uploadedImage.url,
                },
            };
        } else {
            newItem[id] = uploadedImage.url;
        }

        setItem(newItem);
    }

    function handleFiltersChange(e) {
        setFilters({
            ...currentFilters,
            [e.currentTarget.id]: e.currentTarget.value,
        });
    }

    function handleFiltersDateChange(key, value) {
        setFilters({
            ...currentFilters,
            [key]: value?.toISOString(),
        });
    }

    function handleFiltersClear() {
        setFilters({});
    }

    function handleStorySearch(event) {
        let newSelected = JSON.parse(JSON.stringify(currentItem));

        newSelected = {
            ...newSelected,
            alias: event.currentTarget.alias,
            entity_alias: event.currentTarget.alias,
            entity_id: event.currentTarget.value.value,
            value: event.currentTarget.value.value,
            title: event.currentTarget.value.label,
        };

        setItem(newSelected);
    }

    function handleActive(event) {
        const newRows = [];

        Object.assign(newRows, rows);

        const { id, checked, dataset } = event.currentTarget;

        const activeId = id;

        for (let i = 0; i < newRows.length; i++) {
            if (+newRows[i].id === +activeId) {
                newRows[i][dataset.value] = checked;
            } else if (withSingleActive) {
                newRows[i][dataset.value] = false;
            }
        }

        setRows(newRows);
        storeData(newRows);
    }

    function dragEndHandle(e) {
        setTableLoading(true);
        let clonedRows = JSON.parse(JSON.stringify(rows));

        clonedRows = sortRows(clonedRows, e.source.index, e.destination.index);

        setRows(clonedRows);
        storeData(clonedRows);
    }

    function moveItem(e) {
        const { action, id } = e.currentTarget.dataset;

        Fetcher(`${endpoint}/${id}/`, 'PUT', handleData, {
            id,
            priority: action,
            name: '',
        });
    }

    function lockItem(e) {
        setTableLoading(true);
        const { checked, dataset } = e.currentTarget;
        const { id } = dataset;

        const clonedRows = JSON.parse(JSON.stringify(rows));
        const entityObj = clonedRows.find((item) => +item.id === +id);

        entityObj.pos_locked = checked;

        setRows(clonedRows);
        storeData(clonedRows);
    }

    function checkItem(event) {
        const { checked, dataset } = event.currentTarget;
        const { id } = dataset;

        const clonedRows = JSON.parse(JSON.stringify(rows));
        const entityObj = clonedRows.find((item) => +item.id === +id);

        entityObj.pos_checked = checked;

        setRows(clonedRows);
    }

    function deleteCheckedElements() {
        if (window.confirm('Внимание! Вы действительно хотите удалить выбранные пункты?')) {
            removeCheckedItemsFromRows();
        }
    }

    function sortRows(arr, oldIndex, newIndex) {
        let movableItems = structuredClone(arr);

        // Удаляем запись со старого индекса
        const movedItem = movableItems.splice(oldIndex, 1)[0];

        // Помещаем её по новому индексу
        movableItems.splice(newIndex, 0, movedItem);

        // Выносим отдельно все незалоченные записи
        movableItems = movableItems.filter((item) => !item.pos_locked);

        const sortedArray = [];
        arr.forEach((item) => {
            if (item.pos_locked) { // Кладём в новый массив залоченные из оригинального
                sortedArray.push(item);
            } else { // и незалоченные, которые уже отсортированы в нужном порядке
                sortedArray.push(movableItems.shift());
            }
        });

        return sortedArray;
    }

    function validate() {
        const emptyFields = {
            ru: [],
            en: [],
            multi: [],
        };

        formFields.map((field) => {
            if (field.isRequired) {
                if (field.isMultilang) {
                    LANGS.forEach((lang) => {
                        if (!currentItem.content?.[lang]?.[field.name]) {
                            emptyFields[lang].push(field.name);
                        }
                    });
                } else if (!currentItem[field.name]) {
                    emptyFields.multi.push(field.name);
                }
            }
        });

        return {
            isValid: !emptyFields.ru.length && !emptyFields.en.length && !emptyFields.multi.length,
            fields: emptyFields,
        };
    }

    const hasCheckedPoses = rows.filter(({ pos_checked }) => pos_checked);

    return (
        <div className="container-fluid">
            <Row className="align-items-end mb-3 position-relative" style={{ zIndex: 2 }}>
                <Col md={7}>
                    <Row>
                        <PocketForm
                            direction="row"
                            fieldList={filterFields}
                            currentItem={currentFilters}
                            onChange={handleFiltersChange}
                            onDateChange={handleFiltersDateChange}
                            onClear={handleFiltersClear}
                            withClear={!!filterFields.length}
                        />
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col md={7}>
                    {noDrag ? (
                        <ReactTable
                            getTrProps={trProps}
                            className={`-mainmrginformer ${isTableLoading ? 'form_loading' : ''}`}
                            pageSize={itemsCount}
                            showPageSizeOptions={false}
                            showPagination={false}
                            data={Object.keys(currentFilters).length !== 0 ? rowsFiltered : rows}
                            columns={renderColumns({
                                handleActive,
                                lockItem,
                                editItem,
                                deleteItem,
                                moveItem,
                                checkItem,
                            })}
                            pages={1}
                            noDataText="Нет данных"
                        />
                    ) : (
                        <DragDropContext onDragEnd={dragEndHandle}>
                            <ReactTable
                                TrComponent={DragTrComponent}
                                TbodyComponent={DropTbodyComponent}
                                getTrProps={trProps}
                                className={`-mainmrginformer ${isTableLoading ? 'form_loading' : ''}`}
                                pageSize={itemsCount}
                                showPageSizeOptions={false}
                                showPagination={false}
                                data={Object.keys(currentFilters).length !== 0 ? rowsFiltered : rows}
                                columns={renderColumns({
                                    handleActive,
                                    editItem,
                                    deleteItem,
                                    lockItem,
                                    checkItem,
                                })}
                                pages={1}
                                noDataText="Нет данных"
                            />
                        </DragDropContext>
                    )}
                    { hasCheckedPoses.length > 0 ? (
                        <Row className="mt-3 mx-0 justify-content-end">
                            <Button variant="danger" onClick={deleteCheckedElements}>
                                Удалить выбранные
                            </Button>
                        </Row>
                    )
                        : null}
                </Col>
                <Col md={5}>
                    <PocketForm
                        fieldList={formFields}
                        invalidFields={invalidFields}
                        currentItem={currentItem}
                        currentLang={currentLang}
                        onChange={handleChange}
                        onLangDataChange={handleLangDataChange}
                        onImageChange={handleImageChange}
                        onDateChange={handleDateChange}
                        onStorySearch={handleStorySearch}
                    />
                    <Row>
                        <Col md={5}>
                            <ButtonToolbar>
                                <ButtonGroup className="mr-3">
                                    <Button
                                        variant="outline-success"
                                        block
                                        onClick={saveItem}
                                    >
                                        <FontAwesomeIcon
                                            icon="save"
                                        />
                                        {' '}
                                        Сохранить

                                    </Button>
                                </ButtonGroup>

                                <ButtonGroup>
                                    <Button
                                        variant="outline-secondary"
                                        block
                                        onClick={resetItem}
                                    >
                                        <FontAwesomeIcon
                                            icon="redo"
                                        />
                                        {' '}
                                        Отмена

                                    </Button>
                                </ButtonGroup>
                            </ButtonToolbar>
                        </Col>
                        {isMultilang && (
                            <Col md={{ span: 1, offset: 5 }}>
                                <Row className="justify-content-md-end">
                                    <ToggleButtonGroup
                                        value={currentLang}
                                        name="lang"
                                        key="inlineStyleBlock"
                                        type="radio"
                                        onChange={setLang}
                                    >
                                        <ToggleButton variant="outline-secondary" value="ru"> Ru </ToggleButton>
                                        <ToggleButton variant="outline-secondary" value="en"> En </ToggleButton>
                                    </ToggleButtonGroup>
                                </Row>
                            </Col>
                        )}
                    </Row>
                    {renderRightColumn && renderRightColumn()}
                </Col>
            </Row>
        </div>
    );
}

Pocket.defaultProps = {
    formFields: [],
    filterFields: [],
    itemsLimit: 20,
};
